import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Button } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../context/LanguageContext';
import LanguageSwitcher from '../../ui/LanguageSwitcher/LanguageSwitcher';
import './Header.css';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);

      // Update active section based on scroll position
      const sections = document.querySelectorAll('section[id]');
      let current = 'home';
      
      sections.forEach((section) => {
        const sectionTop = (section as HTMLElement).offsetTop - 100;
        const sectionHeight = section.clientHeight;
        if (scrollTop >= sectionTop && scrollTop < sectionTop + sectionHeight) {
          current = section.getAttribute('id') || 'home';
        }
      });
      
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    // Eğer Thank You sayfasındaysak, önce ana sayfaya git
    if (location.pathname === '/thank-you') {
      navigate('/');
      // Ana sayfaya gittikten sonra section'a scroll yap
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Ana sayfadaysak direkt scroll yap
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    
    // Mobil menüyü kapat - Bootstrap class'ını kaldır
    const navbarCollapse = document.getElementById('navbar-nav');
    const navbarToggler = document.querySelector('.navbar-toggler');
    
    if (navbarCollapse && navbarToggler) {
      // Bootstrap collapse class'larını kaldır
      navbarCollapse.classList.remove('show');
      navbarToggler.setAttribute('aria-expanded', 'false');
      
      // Body'den overflow class'ını kaldır (Bootstrap'ın eklediği)
      document.body.classList.remove('modal-open');
    }
  };

  return (
    <Navbar 
      expand="lg" 
      fixed="top" 
      className={`luxury-navbar ${isScrolled ? 'scrolled' : ''}`}
    >
      <Container>
        <Navbar.Brand 
          href="#home" 
          className="navbar-brand"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('home');
          }}
        >
          <img 
            src="assets/img/logo.png" 
            alt="Doç. Dr. Ayşe Sezim Logo" 
            className="navbar-logo"
          />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="navbar-nav" className="navbar-toggler" />
        
        <Navbar.Collapse id="navbar-nav">
          <Nav className="me-auto mb-2 mb-lg-0">
            <Nav.Item>
              <Nav.Link 
                href="#home"
                className={activeSection === 'home' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('home');
                }}
              >
                {t('nav.home')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                href="#treatment-info"
                className={activeSection === 'treatment-info' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('treatment-info');
                }}
              >
                {t('nav.treatment')}
              </Nav.Link>
            </Nav.Item>

            <Nav.Item>
              <Nav.Link 
                href="#about"
                className={activeSection === 'about' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('about');
                }}
              >
                {t('nav.about')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                href="#before-after"
                className={activeSection === 'before-after' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('before-after');
                }}
              >
                {t('nav.beforeAfter')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                href="#testimonials"
                className={activeSection === 'testimonials' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('testimonials');
                }}
              >
                {t('nav.testimonials')}
              </Nav.Link>
            </Nav.Item>

            <Nav.Item>
              <Nav.Link 
                href="#faq"
                className={activeSection === 'faq' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('faq');
                }}
              >
                {t('nav.faq')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                href="#contact"
                className={activeSection === 'contact' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('contact');
                }}
              >
                {t('nav.contact')}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Language Switcher */}
          <LanguageSwitcher 
            currentLanguage={language} 
            onLanguageChange={setLanguage} 
          />

          {/* Social Media Icons */}
          <div className="navbar-social d-none d-lg-flex">
            <a href="https://www.instagram.com/draysesezim/" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-instagram"></i>
            </a>
            <a href="https://www.facebook.com/draysesezim/" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-facebook"></i>
            </a>
            <a href="https://www.youtube.com/@Draysesezim" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-youtube"></i>
            </a>
          </div>

          {/* Call to Action Button */}
          <div className="navbar-cta">
            <Button 
              variant="warning" 
              size="sm"
              onClick={() => scrollToSection('contact')}
              className="cta-button"
            >
              <i className="fas fa-phone me-2"></i>
              {t('cta.free_consultation')}
            </Button>
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;
